/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {z} from 'zod'
import {useTranslation} from 'react-i18next'
import {useMemo} from 'react'
import {CompanyBranchType} from "types/enums/CompanyBranchType";

export const useCompanyBasicSchema = () => {
    const { t } = useTranslation()

    return useMemo(() => z.object({
        name: z
            .string()
            .trim()
            .max(120, t('Company name must be at most 120 characters'))
            .optional(),
        parent_company_id: z.number().nullable().optional(),
        type: z.enum(CompanyBranchType),
        branch: z.string().trim().max(80, t('Branch must be at most 80 characters')).nullable().optional(),
        business_category: z
            .string()
            .trim()
            .max(80, t('Business category must be at most 80 characters'))
            .nullable()
            .optional(),
        employee_size: z
            .string()
            .trim()
            .max(40, t('Employee size must be at most 40 characters'))
            .nullable()
            .optional(),
        employee_count: z
            .string()
            .trim()
            .max(40, t('Employee count must be at most 40 characters'))
            .nullable()
            .optional()
            .or(z.literal('')),
        origin: z.string().optional().nullable().or(z.literal('')),
        tags: z.array(z.string()).optional().nullable(),
        seller_id: z.number().min(1, t('Seller is required')),
        cin: z
            .string()
            .trim()
            .regex(
                /^[A-Za-z0-9-]{0,20}$/u,
                t('CIN must be 2-20 alphanumeric characters')
            )
            .nullable()
            .optional(),
        vat_number: z
            .string()
            .trim()
            .regex(
                /^[A-Za-z0-9-]{0,20}$/u,
                t('VAT number must be 2-20 alphanumeric characters')
            )
            .nullable()
            .optional(),
        notes: z
            .string()
            .trim()
            .max(1000, t('Notes must be at most 1000 characters'))
            .nullable()
            .optional(),
        referral_id: z
            .string()
            .trim()
            .max(120, t('Referral ID must be at most 120 characters'))
            .nullable()
            .optional(),
        website: z
            .string()
            .url(t('Invalid URL format'))
            .nullable()
            .or(z.literal(''))
            .optional(),
        first_name: z
            .string()
            .trim()
            .max(60, t('First name must be at most 60 characters'))
            .nullable()
            .optional()
            .or(z.literal('')),
        last_name: z
            .string()
            .trim()
            .max(60, t('Last name must be at most 60 characters'))
            .nullable()
            .optional()
            .or(z.literal('')),
        email: z
            .string()
            .email(t('Invalid email format'))
            .nullable()
            .optional()
            .or(z.literal('')),
        phone: z
            .string()
            // .regex(/^[+()0-9\s-]*$/u, t('Phone must contain valid characters'))
            .max(30, t('Phone must be at most 30 characters'))
            .nullable()
            .optional()
            .or(z.literal('')),
    }).superRefine((data, context) => {
        if (data.type !== CompanyBranchType.Private && (!data.name || data.name.length < 2)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['name'],
                message: t('Company name must be at least 2 characters'),
            })
        }
    }), [t])
}

export type CompanyBasicInputs = z.infer<ReturnType<typeof useCompanyBasicSchema>>
